import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  API,
  CompanyAttributeDTO,
  FixedAspectRatioImageDTO,
  FramedReportingDTO,
  LocationFullDTO,
  LocationOnlineStatus,
  SingleFileUpload,
  VisitorTrackerDTO,
} from 'src/app/services/api/api';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ImageUploadModel } from 'src/app/services/gui/image/model';
import { Clipping } from 'src/app/components/image/model';
import { ImageUploadService } from 'src/app/services/gui/image/image-upload.service';
import { LocationFullDTOAndTags } from '../../../models/taggable.model';
import { TagService } from '../../../services/tag.service';
import { PermissionService } from '../../../services/permission.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-location-details',
  templateUrl: './location-details.component.html',
  styleUrls: ['./location-details.component.scss'],
  standalone: false,
})
export class LocationDetailsComponent implements OnInit, OnDestroy {
  locationId: number;
  location: LocationFullDTOAndTags;
  locationReporting: FramedReportingDTO;

  isPmgClient: boolean;
  avatarUrl: string;
  headerUrl: string;

  visitorTracker: VisitorTrackerDTO[];

  logoImageUpload: ImageUploadModel;
  headerImageUpload: ImageUploadModel;
  animationOptions: { autoplay: boolean; loop: boolean };
  animateState: number;

  constructor(
    private readonly api: API,
    private readonly activatedRoute: ActivatedRoute,
    private readonly modal: ModalService,
    private readonly imageUploadService: ImageUploadService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    public readonly tagService: TagService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.headerImageUpload = new ImageUploadModel();
    this.locationId = undefined;
    this.location = undefined;
    this.locationReporting = undefined;
    this.isPmgClient = false;
    this.avatarUrl = './assets/img/no-image.png';
    this.headerUrl = './assets/img/location-placeholder-bg.jpg';
    this.visitorTracker = [];
    this.animationOptions = { autoplay: true, loop: true };
    this.animateState = 0;
  }

  ngOnInit() {
    this.isPmgClient = this.util.getSeatbackCMSClient() === 'pmg';
    this.activatedRoute.params.subscribe(params => {
      this.locationId = +params.locationID;
      this.refreshFromServer();
    });

    setInterval(() => {
      this.animateState++;
      if (this.animateState === 2) {
        this.animateState = 0;
      }
    }, 15000);
  }

  ngOnDestroy(): void {
    this.logoImageUpload?.destroySubscriptions();
    this.headerImageUpload?.destroySubscriptions();
  }

  refreshFromServer(): void {
    this.api
      .ss_fm_Location_retrieveById(this.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (e: LocationFullDTO) => {
          this.location = e;
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            const locations = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Location, [this.location]);
            this.location = locations[0];
          }
          this.avatarUrl = this.location?.logo
            ? this.util.appendQueryParamToUrl(this.location.logo.url as unknown as string, 'thumbnail=L')
            : './assets/img/no-image.png';
          this.headerUrl = this.location?.headerImage
            ? this.util.appendQueryParamToUrl(this.location.headerImage.url as unknown as string, '')
            : './assets/img/location-placeholder-bg.jpg';
          if (
            this.permissionService.hasPermission(this.permissionService.Permission.location_visitor_aggregation) &&
            this.permissionService.hasPermission(this.permissionService.Permission.visitor_tracker_list)
          ) {
            this.api
              .dm_visitor_VisitorTracker_retrieveAll({ location_id: this.location.id })
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: data => {
                  this.visitorTracker = data;
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
            this.api
              .ss_fm_Location_visitorAggregation(this.location.id, { productive: true })
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: (data: FramedReportingDTO) => {
                  this.locationReporting = data;
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }

  pickLogo() {
    this.logoImageUpload?.destroySubscriptions();
    this.api
      .ss_fm_Location_retrieveById(this.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (location: LocationFullDTO) => {
          this.logoImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.logo'));
          this.logoImageUpload.imageTransfer.image = location?.logo;
          this.logoImageUpload.subClippingChange = this.logoImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.logoClippingChange(res);
          });
          this.logoImageUpload.subFileChange = this.logoImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.logoFileChange(file);
          });
          this.logoImageUpload.subDeletePress = this.logoImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.logoDeletePress();
          });
          this.logoImageUpload.subModalClosed = this.logoImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.logoUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.logoImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.logoImageUpload.imageTransfer.image;
      this.logoImageUpload.imageTransfer.image = null;
      this.logoImageUpload.imageTransfer.uploading = true;
      this.api
        .ss_fm_Location_clipLogo(this.locationId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.logoImageUpload.imageTransfer.image = image;
            this.logoImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  logoFileChange(file: SingleFileUpload) {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_fm_Location_uploadLogo(this.locationId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.logoImageUpload.imageTransfer.image = res;
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoDeletePress() {
    this.logoImageUpload.imageTransfer.image = null;
    this.logoImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_fm_Location_deleteLogo(this.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.logoImageUpload.imageTransfer.image = null;
          }
          this.logoImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  logoUploadModalClosed() {
    this.logoImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  pickHeader() {
    this.headerImageUpload?.destroySubscriptions();
    this.api
      .ss_fm_Location_retrieveById(this.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (location: LocationFullDTO) => {
          this.headerImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.header'));
          this.headerImageUpload.imageTransfer.image = location?.headerImage;
          this.headerImageUpload.subClippingChange = this.headerImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.headerClippingChange(res);
          });
          this.headerImageUpload.subFileChange = this.headerImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.headerFileChange(file);
          });
          this.headerImageUpload.subDeletePress = this.headerImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.headerDeletePress();
          });
          this.headerImageUpload.subModalClosed = this.headerImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.headerUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.headerImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.headerImageUpload.imageTransfer.image;
      this.headerImageUpload.imageTransfer.image = null;
      this.headerImageUpload.imageTransfer.uploading = true;
      this.api
        .ss_fm_Location_clipHeaderImage(this.locationId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.headerImageUpload.imageTransfer.image = image;
            this.headerImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  headerFileChange(file: SingleFileUpload) {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_fm_Location_uploadHeaderImage(this.locationId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.headerImageUpload.imageTransfer.image = res;
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerDeletePress() {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_fm_Location_deleteHeaderImage(this.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.headerImageUpload.imageTransfer.image = null;
          }
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerUploadModalClosed() {
    this.headerImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  onlineStatusText() {
    if (this.location) {
      switch (this.location.onlineStatus) {
        case LocationOnlineStatus.Unknown:
          return 'Unknown';
        case LocationOnlineStatus.AllOnline:
          return 'Online';
        case LocationOnlineStatus.Online:
          return 'Online';
        case LocationOnlineStatus.MostlyOnline:
          return 'Mixed';
        case LocationOnlineStatus.AllCurrentlyOffline:
          return 'Mixed';
        case LocationOnlineStatus.MostlyOffline:
          return 'Mixed';
        case LocationOnlineStatus.Offline:
          return 'Offline';
        case LocationOnlineStatus.AllOffline:
          return 'Offline';
        default:
          return '???????';
      }
    }
  }

  getParentCust(): string {
    if (!this.location?.managedBy?.attributes || !this.isPmgClient) {
      return null;
    }
    const parentCust: CompanyAttributeDTO = this.location.managedBy.attributes.find(x => x.name === 'SAP-ParentCustName');
    if (!parentCust) {
      return null;
    }

    const parentCustVal = parentCust.value.toLowerCase();
    if (parentCustVal.indexOf('total') > -1) {
      return 'total';
    }
    if (parentCustVal.indexOf('shell') > -1) {
      return 'shell';
    }
    if (parentCustVal.indexOf('aral') > -1) {
      return 'aral';
    }
    if (parentCustVal.indexOf('orlen') > -1) {
      return 'orlen';
    }
    if (parentCustVal.indexOf('anton willer') > -1) {
      return 'bft';
    }
    return null;
  }

  onlineStatusColor() {
    if (this.location) {
      switch (this.location.onlineStatus) {
        case LocationOnlineStatus.Unknown:
          return 'white grey';
        case LocationOnlineStatus.AllOnline:
          return 'white green';
        case LocationOnlineStatus.Online:
          return 'white green';
        case LocationOnlineStatus.MostlyOnline:
          return 'white yellow';
        case LocationOnlineStatus.AllCurrentlyOffline:
          return 'white yellow';
        case LocationOnlineStatus.MostlyOffline:
          return 'white yellow';
        case LocationOnlineStatus.Offline:
          return 'white red';
        case LocationOnlineStatus.AllOffline:
          return 'white red';
        default:
          return '???????';
      }
    }
  }
}
